import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import MainLayout from "../../layouts/Layout";
import { ProjectOutlined, FolderAddOutlined, FileAddOutlined, FilterOutlined, CalendarOutlined, TeamOutlined, UserOutlined, DownOutlined, MenuOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import { Button, Row, Col, Dropdown, Select, Input } from "antd";
import { useAntdAppMessage } from "../../hooks/useAntdAppMessage";
import type { MenuProps } from "antd";
import styles from "./Projects.module.scss";
import AddNewFolderModal from "../../modals/addNewFolderModal";
import AddNewFileModal from "../../modals/addNewFileModal";
import { useLanguage } from "../../contexts/LanguageContext";
import FileDetail from "./FileDetail";
import FooterActions from "./FooterActions";
import ApprovalModal from "../../modals/approvalModal";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import NewEntrySection from "../../components/NewEntrySection/NewEntrySection";
import ProjectItemCard, { type ProjectItemCardMenuAction, type ProjectItemCardMoveDndConfig } from "../../components/ProjectItemCard/ProjectItemCard";
import AddNewProjectModal from "../../modals/addNewProjectModal";
import ProjectFolderEmptyState from "../../components/ProjectFolderEmptyState/ProjectFolderEmptyState";
import HeaderlessLayout from "../../layouts/HeaderlessLayout";
import MoveToModal from "../../components/MoveToModal/MoveToModal";
import UploadProgressCard, { type UploadProgressItem } from "../../components/UploadProgressCard/UploadProgressCard";
import UploadLeaveWarningModal from "../../components/UploadLeaveWarningModal/UploadLeaveWarningModal";
import UploadFileTypeWarningModal from "../../components/UploadFileTypeWarningModal/UploadFileTypeWarningModal";
import {
  canMoveOrDuplicateEntry,
  hasRole,
  Role,
  ROLES_CONTENT_EDITOR,
  ROLES_PROJECT_ORG_ADMIN,
} from "../../constants/roles";
import { getFilesWithRelativePaths } from "../../utils/fileDropHelpers";
import { allocateUniqueDropName, dropTopLevelModeForName } from "../../utils/uniqueDropTopLevelName";
import { getVideoThumbnailAtSecond } from "../../utils/videoThumbnail";
import { fileNameWithoutExtension } from "../../utils/stringHelpers";
import { type DownloadItemsFetchers, getDownloadItemsForEntry, triggerDownloadsFromItems } from "../../utils/downloadFromLinks";
import { ApiFilePostStatusEnum } from "../../service/apis/FileApi";
import { formatBytes } from "../../helpers/converters";
import {
  defaultSortDirectionForField,
  entryMatchesOrganizationFilter,
  getEntryChildrenCount,
  getEntryOrganizationLabel,
  getEntrySortStatusLabel,
} from "../../utils/projectFilterSortHelpers";
import { ENTRY_MOVE_DRAG_MIME, setEntryMoveDragData, readEntryMoveDragData } from "../../utils/entryMoveDnD";
import { isApiAccessDenied } from "../../utils/apiAccess";
import { getProjectFolderVariantFromLocation } from "../../utils/projectScope";

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

const ALLOWED_UPLOAD_EXTENSIONS = ['mp4', 'png', 'jpeg', 'jpg', 'svg', 'pdf'];
const ALLOWED_UPLOAD_ACCEPT = '.mp4,.png,.jpeg,.jpg,.svg,.pdf,video/mp4,image/png,image/jpeg,image/svg+xml,application/pdf';

function getFileExtension(fileName: string): string {
  const name = String(fileName || '').trim();
  const lastDot = name.lastIndexOf('.');
  if (lastDot < 0) return '';
  return name.slice(lastDot + 1).toLowerCase();
}

const ProjectDetail = () => {
  const message = useAntdAppMessage();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEntryByPath, getFileById, getDownloadLinks, deleteEntry, duplicateEntry, moveEntry, editFile, editFolder, editProject, uploadWithPath, createFolder, createFile } = useSecureService();

  const [entry, setEntry] = useState<any>(null);
  const [pathAccessDenied, setPathAccessDenied] = useState(false);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isEntryPopoverOpen, setIsEntryPopoverOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  /** Kart menüsünden Assign/Delete — checkbox seçmeden hedef id’ler */
  const [menuDrivenSelection, setMenuDrivenSelection] = useState<Set<number> | null>(null);
  const [, setIsSelectMode] = useState(false);
  
  // Approval modal state'leri
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  // Edit modal state'leri
  const [isEditFileModalVisible, setIsEditFileModalVisible] = useState(false);
  const [isEditFolderModalVisible, setIsEditFolderModalVisible] = useState(false);
  const [isEditProjectModalVisible, setIsEditProjectModalVisible] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  // Filter & Sort (project detail toolbar)
  const [filterDateType, setFilterDateType] = useState<string>('');
  const [filterOrganization, setFilterOrganization] = useState('');
  const [filterName, setFilterName] = useState('');
  const [sortField, setSortField] = useState<
    'creationDate' | 'organization' | 'name' | 'numberOfFiles' | 'status' | ''
  >('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | ''>('');
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [openSortDropdown, setOpenSortDropdown] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [changeCoverTarget, setChangeCoverTarget] = useState<any>(null);
  const changeCoverInputRef = useRef<HTMLInputElement>(null);
  /** Klasör içindeyken sağ tık menüden "Rename" ile açılan edit modal için mevcut klasör */
  const [editingFolderEntry, setEditingFolderEntry] = useState<any>(null);
  const [openMoveToModal, setOpenMoveToModal] = useState(false);
  const [moveToEntry, setMoveToEntry] = useState<any>(null);
  const [moveToLoading, setMoveToLoading] = useState(false);
  const [uploadFileTypeWarningVisible, setUploadFileTypeWarningVisible] = useState(false);
  const [uploadFileTypeWarningDisallowed, setUploadFileTypeWarningDisallowed] = useState<string[]>([]);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);
  /** Boş klasörde dosya seçici açıkken empty state soluk + LineBG kapalı */
  const [isEmptyStateFilePickerOpen, setIsEmptyStateFilePickerOpen] = useState(false);

  // Drag-and-drop upload / duplicate: her biri ayrı progress card. contextPath = işlemin başladığı path.
  type UploadSession = { id: string; items: UploadProgressItem[]; isFolderDrop: boolean; isComplete: boolean; contextPath: string; sessionType?: 'upload' | 'duplicate' };
  const [uploadSessions, setUploadSessions] = useState<UploadSession[]>([]);
  const uploadCancelRefs = useRef<Record<string, boolean>>({});
  const currentPathRef = useRef<string>('');

  


  const pathParts = location.pathname.split('/').filter(p => p && p.trim() !== '');
  const fullPath = pathParts.join('/');
  currentPathRef.current = fullPath;
  const currentPathSessions = useMemo(() => uploadSessions.filter((s) => s.contextPath === fullPath), [uploadSessions, fullPath]);
  const hasActiveUploads = useMemo(
    () => uploadSessions.some((s) => s.items.some((i) => i.status === "uploading" || i.status === "pending")),
    [uploadSessions]
  );

  useEffect(() => {
    if (!hasActiveUploads) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t("uploadLeaveWarning.message");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasActiveUploads, t]);

  useEffect(() => {
    if (!isEmptyStateFilePickerOpen) return;
    const onWinFocus = () => setIsEmptyStateFilePickerOpen(false);
    window.addEventListener("focus", onWinFocus, true);
    return () => window.removeEventListener("focus", onWinFocus, true);
  }, [isEmptyStateFilePickerOpen]);

  const [showLeaveWarningModal, setShowLeaveWarningModal] = useState(false);
  const leaveWarningProceedRef = useRef<(() => void) | null>(null);
  const pathToStayRef = useRef("");

  useEffect(() => {
    if (!hasActiveUploads) return;
    pathToStayRef.current = window.location.pathname + window.location.search + window.location.hash;
    const handlePopState = () => {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      if (currentUrl === pathToStayRef.current) {
        return;
      }
      setShowLeaveWarningModal(true);
      leaveWarningProceedRef.current = () => {
        setShowLeaveWarningModal(false);
        leaveWarningProceedRef.current = null;
        window.history.back();
      };
      window.history.pushState({ uploadLeaveWarning: true }, "", pathToStayRef.current);
    };
    window.history.pushState({ uploadLeaveWarning: true }, "");
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasActiveUploads]);

  const getProjectsBasePath = () => {
    const fromState = (location.state as any)?.fromProjectsBasePath;
    if (fromState === '/internal-projects' || fromState === '/projects' || fromState === '/external-projects') {
      return fromState;
    }
    const saved = localStorage.getItem('lastProjectsBasePath');
    if (saved === '/internal-projects' || saved === '/projects' || saved === '/external-projects') {
      return saved;
    }
    return '/internal-projects';
  };

  const folderCardVariant = getProjectFolderVariantFromLocation(location);
  const sortSelectPopupClassName = `${styles.projectDetailSortSelectPopup}${folderCardVariant === 'external' ? ` ${styles.projectDetailSortSelectPopupExternal}` : ''}`;

  const canUseEmptyStateActions = user?.role !== Role.Viewer;
  const canCreateFolderFromEmptyState = useMemo(
    () => hasRole(user?.role, ...ROLES_CONTENT_EDITOR),
    [user?.role]
  );

  /** Taşı / kopyala: Admin, SuperAdmin, Editor + projectId */
  const canMoveOrDuplicate = useMemo(
    () => canMoveOrDuplicateEntry(user?.role) && projectId != null,
    [user?.role, projectId]
  );

  const emptyEntrySectionTitle = useMemo(() => {
    if (!entry) return t('projectDetail.emptyFolderHeading');
    const et = entry.entryType;
    if (et === 'Project' || String(et) === 'Project') {
      return t('projectDetail.emptyProjectHeading');
    }
    return t('projectDetail.emptyFolderHeading');
  }, [entry, t]);

  const activeUploadTopLevelNames = useMemo(() => {
    const set = new Set<string>();
    uploadSessions.forEach((s) => {
      if (!s.isFolderDrop) return;
      const hasActive = s.items.some((i) => i.status === "uploading" || i.status === "pending");
      if (!hasActive) return;
      s.items.forEach((item) => {
        const top = item.relativePath.split("/")[0];
        if (top) set.add(top);
      });
    });
    return set;
  }, [uploadSessions]);

  // Filtrelenmiş ve sıralanmış assets/folders (en az 2 karakterde org/name filtresi otomatik)
  const { filteredAssets, filteredFolders } = useMemo(() => {
    const children = entry?.children;
    if (!Array.isArray(children)) return { filteredAssets: [], filteredFolders: [] };
    const rawFolders = children.filter((c: any) => c.entryType === 'Folder' || String(c.entryType) === 'Folder');
    const rawAssets = children.filter((c: any) => c.entryType === 'File' || c.entryType === 2 || String(c.entryType) === 'File');
    const foldersExcludingUploading = rawFolders.filter((c: any) => !activeUploadTopLevelNames.has(String(c.name || '')));

    const now = new Date();
    const inCurrentMonth = (d: Date) => d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const inCurrentQuarter = (d: Date) => {
      const q = Math.floor(d.getMonth() / 3) + 1;
      const nq = Math.floor(now.getMonth() / 3) + 1;
      return d.getFullYear() === now.getFullYear() && q === nq;
    };
    const inCurrentYear = (d: Date) => d.getFullYear() === now.getFullYear();

    const applyFilters = (list: any[]) =>
      list.filter((item: any) => {
        if (filterDateType) {
          const dateStr = item.updatedAt || item.createdAt;
          if (!dateStr) return false;
          const d = new Date(dateStr);
          if (filterDateType === 'month' && !inCurrentMonth(d)) return false;
          if (filterDateType === 'quarter' && !inCurrentQuarter(d)) return false;
          if (filterDateType === 'year' && !inCurrentYear(d)) return false;
        }
        if (!entryMatchesOrganizationFilter(item, filterOrganization.trim())) return false;
        if (filterName.trim().length >= 2) {
          if (!String(item.name || '').toLowerCase().includes(filterName.trim().toLowerCase())) return false;
        }
        return true;
      });

    const sortList = (list: any[]) => {
      if (!sortField) return list;
      const arr = [...list];
      const effectiveDir = sortDirection || 'desc';
      const mult = effectiveDir === 'asc' ? 1 : -1;
      if (sortField === 'creationDate') arr.sort((a, b) => mult * (new Date(a.updatedAt || a.createdAt || 0).getTime() - new Date(b.updatedAt || b.createdAt || 0).getTime()));
      else if (sortField === 'organization')
        arr.sort((a, b) => mult * getEntryOrganizationLabel(a).localeCompare(getEntryOrganizationLabel(b)));
      else if (sortField === 'name') arr.sort((a, b) => mult * (a.name || '').localeCompare(b.name || ''));
      else if (sortField === 'numberOfFiles')
        arr.sort((a, b) => mult * (getEntryChildrenCount(a) - getEntryChildrenCount(b)));
      else if (sortField === 'status')
        arr.sort((a, b) =>
          mult *
          getEntrySortStatusLabel(a).localeCompare(getEntrySortStatusLabel(b), undefined, { sensitivity: 'base' })
        );
      return arr;
    };

    return {
      filteredAssets: sortList(applyFilters(rawAssets)),
      filteredFolders: sortList(applyFilters(foldersExcludingUploading)),
    };
  }, [entry?.children, filterDateType, filterOrganization, filterName, sortField, sortDirection, activeUploadTopLevelNames]);

  // Path servisinden gelen entry'nin kendi totalSizeInBytes değeri (en alttaki ana total size)
  const totalSizeBytes = entry?.totalSizeInBytes ?? 0;

  // Boş alana sağ tık: her zaman Create Folder / Create File (menü bağımsız)
  const contentAreaMenuItems: MenuProps['items'] = useMemo(() => {
    const items: NonNullable<MenuProps['items']> = [];
    if (hasRole(user?.role, ...ROLES_CONTENT_EDITOR)) {
      items.push({ key: 'folder', label: t('projectDetail.createFolder'), icon: <FolderAddOutlined /> });
    }
    items.push({ key: 'file', label: t('projectDetail.createFile'), icon: <FileAddOutlined /> });
    return items;
  }, [user?.role, t]);

  const triggerFileUpload = () => {
    if (Array.isArray(entry?.children) && entry.children.length === 0) {
      setIsEmptyStateFilePickerOpen(true);
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEmptyStateFilePickerOpen(false);
    const files = e.target.files;
    if (!files?.length || !projectId) {
      e.target.value = '';
      return;
    }
    const valid: { file: File; relativePath: string }[] = [];
    let skipped = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_FILE_SIZE) skipped++;
      else valid.push({ file, relativePath: file.name });
    }
    e.target.value = '';
    if (skipped > 0) message.warning(t('projects.fileUpload.fileSizeExceeded'));
    if (valid.length > 0) {
      const existingNamesLower = new Set(
        (entry?.children || []).map((c: any) => fileNameWithoutExtension(String(c?.name ?? '').trim()).toLowerCase()).filter(Boolean)
      );
      const newNames = valid.map(({ relativePath, file: f }) => fileNameWithoutExtension(relativePath || f.name).toLowerCase());
      const hasDuplicateWithExisting = newNames.some((name) => existingNamesLower.has(name));
      const hasDuplicateInBatch = new Set(newNames).size !== newNames.length;
      if (hasDuplicateWithExisting || hasDuplicateInBatch) {
        message.warning(t('projects.errors.sameFileNameExists'));
        return;
      }
      const baseId = `upload-${Date.now()}`;
      runCreateFileQueue(`${baseId}-file`, valid, projectId, folderIdForUpload, fullPath);
    }
  };

  const handleCreateFolder = async () => {
    if (!projectId) return;
    const parentId = entry?.entryType === 'Project' ? undefined : entry?.id;
    try {
      const result = await createFolder({
        name: null as any,
        parentId,
        projectId,
      });
      if (result?.success) {
        handleRefresh(true);
      } else {
        message.error(t('projects.errors.createFolderError'));
      }
    } catch {
      message.error(t('projects.errors.createFolderError'));
    }
  };

  const handleContentAreaMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'folder') handleCreateFolder();
    else if (key === 'file') triggerFileUpload();
  };

  const renderEmptyFolderState = () => (
    <>
      <div className={styles.projectDetailSection}>
        <h3
          className={`${styles.projectDetailSectionTitle} ${
            folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''
          }`}
        >
          {emptyEntrySectionTitle}
        </h3>
      </div>
      <Row className={styles.projectDetailEmptyWrap}>
        <Col span={24}>
          <ProjectFolderEmptyState
            variant={folderCardVariant === 'external' ? 'external' : 'internal'}
            title={t('projectDetail.emptyStateTitle')}
            hint={
              canUseEmptyStateActions
                ? t('projectDetail.emptyStateHint')
                : t('projectDetail.emptyStateHintViewer')
            }
            uploadLabel={t('projectDetail.emptyStateUploadFile')}
            createFolderLabel={t('projectDetail.createFolder')}
            onUploadFile={triggerFileUpload}
            onCreateFolder={handleCreateFolder}
            showUpload={canUseEmptyStateActions}
            showCreateFolder={canUseEmptyStateActions && canCreateFolderFromEmptyState}
            interactionDimmed={isDraggingOver || isEmptyStateFilePickerOpen}
          />
        </Col>
      </Row>
    </>
  );

  const handleRefresh = async (silent?: boolean) => {
    if (!fullPath) {
      navigate(getProjectsBasePath());
      return;
    }
    if (!silent) setLoading(true);
    setPathAccessDenied(false);
    try {
      const response = await getEntryByPath(fullPath);
      
      if (response.success && response.data) {
          const entryData = response.data;
          
          // projectId'yi set et - Entry type'a göre belirle
          if ((entryData as any).entryType === 'Project') {
            // Project ise id'si projectId olarak kullanılır
            setProjectId((entryData as any).id);
          } else {
            // Folder ise servisten gelen projectId kullanılır
            setProjectId((entryData as any).projectId);
          }

        // Eğer bu bir dosya ise ve versions bilgisi yoksa, getFileById ile tam detayını al
        if ((entryData as any).entryType === 2 || ((entryData as any).entryType && String((entryData as any).entryType) === 'File')) {
          if (entryData.id) {
            try {
              const fileResponse = await getFileById(entryData.id);
              if (fileResponse.success && fileResponse.data) {
                // entryType ve path'i koru, getFileById'den gelen data'da entryType ve path yanlış olabilir
                setEntry({ 
                  ...fileResponse.data, 
                  entryType: entryData.entryType,
                  path: entryData.path || fileResponse.data.path // EntryData'dan gelen path'i öncelikle kullan
                });
              } else if (isApiAccessDenied(fileResponse)) {
                setPathAccessDenied(true);
                setEntry(null);
              } else {
                setEntry(entryData);
              }
            } catch (error) {
              // getFileById error for main entry
              setEntry(entryData);
            }
          } else {
            setEntry(entryData);
          }
        } else {
          // Klasör/proje: getEntryByPath'in döndürdüğü children yeterli; her file için ayrı getFileById atılmıyor (detay sadece dosyaya girildiğinde FileDetail'de alınır)
          setEntry(entryData);
        }
      } else {
        if (isApiAccessDenied(response)) {
          setPathAccessDenied(true);
        }
        setEntry(null);
      }
    } catch (error) {
      setEntry(null);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleRefreshRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  handleRefreshRef.current = handleRefresh;

  const siblingIdsForMove = useMemo(() => {
    const children = entry?.children;
    if (!Array.isArray(children)) return new Set<number>();
    return new Set(
      children.map((c: any) => c.id).filter((id: unknown): id is number => typeof id === 'number')
    );
  }, [entry?.children]);

  const onMoveDragStart = useCallback((e: React.DragEvent, item: any) => {
    const isFile =
      item.entryType === 'File' || item.entryType === 2 || String(item.entryType) === 'File';
    const sourceEntryType = isFile ? ('File' as const) : ('Folder' as const);
    setEntryMoveDragData(e.dataTransfer, { sourceId: item.id, sourceEntryType });
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onMoveDragEnd = useCallback(() => {
    setDragOverFolderId(null);
  }, []);

  const onMoveFolderDragOver = useCallback((e: React.DragEvent, folderItem: any) => {
    const types = e.dataTransfer.types ? Array.from(e.dataTransfer.types as unknown as string[]) : [];
    if (!types.includes(ENTRY_MOVE_DRAG_MIME) && !types.includes('text/plain')) return;
    e.preventDefault();
    e.stopPropagation();
    const payload = readEntryMoveDragData(e.dataTransfer);
    if (payload && payload.sourceId === folderItem.id) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderItem.id);
  }, []);

  const onMoveFolderDragLeave = useCallback((e: React.DragEvent, folderItem: any) => {
    e.stopPropagation();
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDragOverFolderId((id) => (id === folderItem.id ? null : id));
  }, []);

  const onMoveFolderDrop = useCallback(
    async (e: React.DragEvent, targetFolder: any) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverFolderId(null);
      const payload = readEntryMoveDragData(e.dataTransfer);
      if (!payload) return;
      if (payload.sourceId === targetFolder.id) return;
      const isTargetFolder =
        targetFolder.entryType === 'Folder' || String(targetFolder.entryType) === 'Folder';
      if (!isTargetFolder) return;
      if (!siblingIdsForMove.has(payload.sourceId) || !siblingIdsForMove.has(targetFolder.id)) return;
      if (projectId == null) {
        message.error(t?.('projects.errors.invalidTarget') ?? 'Invalid destination.');
        return;
      }
      try {
        const result = await moveEntry({
          sourceId: payload.sourceId,
          sourceEntryType: payload.sourceEntryType,
          targetProjectId: projectId,
          targetParentId: targetFolder.id,
        });
        if (result?.success) {
          message.success(t?.('projects.moveSuccess') ?? 'Moved successfully.');
          await handleRefreshRef.current(true);
        } else {
          message.error(result?.message ?? (t?.('projects.errors.moveError') ?? 'Failed to move.'));
        }
      } catch {
        message.error(t?.('projects.errors.moveError') ?? 'Failed to move.');
      }
    },
    [siblingIdsForMove, projectId, moveEntry, t]
  );

  const entryMoveDndBase = useMemo(
    (): Omit<ProjectItemCardMoveDndConfig, 'isActiveMoveDropTarget'> => ({
      onMoveDragStart,
      onMoveDragEnd,
      onMoveFolderDragOver,
      onMoveFolderDragLeave,
      onMoveFolderDrop,
    }),
    [onMoveDragStart, onMoveDragEnd, onMoveFolderDragOver, onMoveFolderDragLeave, onMoveFolderDrop]
  );

  useEffect(() => {
    setSelectedItems(new Set());
    setMenuDrivenSelection(null);
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname change only, handleRefresh intentionally excluded
  }, [location.pathname]);





  const handleNavigation = (item: any) => {
    // Path kontrolü - path yoksa veya boşsa navigate etme
    if (!item || !item.path || typeof item.path !== 'string' || item.path.trim() === '') {
      return;
    }
    
    // Path'i temizle ve normalize et
    const cleanPath = item.path.trim();
    const newPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    
    // Dosya kartında her zaman File EntryPath'e git (UUID path), versiyonda seçim FileDetail içinde yapılır
    if (item.entryType === 'File' || String(item.entryType) === '2') {
      navigate(newPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
      return;
    }
    // Klasör/Proje için path-based navigation
    navigate(newPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
  };

  const handleGoBack = () => {
    const parentPath = `/${pathParts.slice(0, -1).join('/')}`;
    if (pathParts.length > 1) {
      navigate(parentPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
    } else {
      navigate(getProjectsBasePath());
    }
  };

  // Toplu seçim — Admin, SuperAdmin
  const canMultiSelect = () => {
    if (!user) return false;
    return hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);
  };

  const handleItemSelect = (itemId: number, checked: boolean) => {
    if (checked) {
      setMenuDrivenSelection(null);
      // Toplu seçim kontrolü - sadece yetkili roller birden fazla seçim yapabilir
      if (selectedItems.size > 0 && !canMultiSelect()) {
        // Yetkisiz kullanıcı zaten bir öğe seçiliyse yeni seçim yapamaz
        message.warning('You can only make single selections. You do not have permission for multi-selection.');
        return;
      }
      
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.add(itemId);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.delete(itemId);
      setSelectedItems(newSelectedItems);
    }
  };


  const handleBulkDelete = async () => {
    try {
      const selectionForDelete =
        selectedItems.size > 0 ? selectedItems : menuDrivenSelection ?? new Set<number>();
      if (selectionForDelete.size > 0) {
        // Seçilen öğeleri sil
       
        
        // Seçili öğelerin detaylarını al
        const selectedItemsDetails = entry?.children
          ?.filter((item: any) => selectionForDelete.has(item.id))
          .map((item: any) => ({
            id: item.id,
            entryType: item.entryType
          })) || [];
        
        if (selectedItemsDetails.length === 0) {
          throw new Error('No items to delete');
        }
        
        // deleteEntry API çağrısı yap
        const response = await deleteEntry(selectedItemsDetails);
        if (!response.success) {
          throw new Error(`Failed to delete items: ${response.message}`);
        }
        
        // Başarılı silme sonrası state'i temizle ve sayfayı yenile
        setSelectedItems(new Set());
        setMenuDrivenSelection(null);
        setIsSelectMode(false);
        
        // Success mesajı göster
        message.success('Selected items deleted successfully.');
        
        // Sayfayı yenile
        await handleRefresh();
      } else {
        // Seçili öğe yoksa mevcut klasörü sil
        
        if (!entry?.id) {
          throw new Error('No entry to delete');
        }
        
        const response = await deleteEntry([{
          id: entry.id,
          entryType: entry.entryType
        }]);
        
        if (!response.success) {
          throw new Error(`Failed to delete entry: ${response.message}`);
        }
        
        // Success mesajı göster
        message.success(`${entry.name} deleted successfully.`);
        
        // Ana sayfaya yönlendir
        navigate(getProjectsBasePath());
      }
      
    } catch (error) {
      // Delete error
      throw error; // FooterActions'da handle edilecek
    }
  };

  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) return;
    const selected = entry?.children?.filter((c: any) => selectedItems.has(c.id)) ?? [];
    const fetchers: DownloadItemsFetchers = { getFileById, getDownloadLinks: getDownloadLinks as DownloadItemsFetchers['getDownloadLinks'] };
    try {
      let totalItems: Array<{ name?: string | null; url?: string | null }> = [];
      for (const item of selected) {
        const items = await getDownloadItemsForEntry(
          { id: item.id, path: item.path, entryType: item.entryType },
          fetchers
        );
        totalItems = totalItems.concat(items);
      }
      if (totalItems.length === 0) {
        message.warning('No downloadable files found for the selected items.');
        return;
      }
      await triggerDownloadsFromItems(totalItems);
      message.success('Download started.');
    } catch (error) {
      console.error('[Download] handleBulkDownload error:', error);
      message.error('Download failed. Please try again.');
    }
  };

  const handleBulkCopyUrl = async () => {
    try {
      if (selectedItems.size > 0) {
        // Seçilen öğelerin URL'lerini kopyala
       
        
        // Seçili öğelerin URL'lerini oluştur
        const urls = entry?.children
          ?.filter((item: any) => selectedItems.has(item.id))
          .map((item: any) => {
            const itemPath = `/${item.path}`;
            return `${window.location.origin}${itemPath}`;
          }) || [];
        
        if (urls.length > 0) {
          const urlText = urls.join('\n');
          await navigator.clipboard.writeText(urlText);
          message.success(`URLs of ${urls.length} items copied.`);
        }
      } else {
        // Seçili öğe yoksa mevcut sayfanın URL'sini kopyala
        const currentUrl = window.location.href;
        await navigator.clipboard.writeText(currentUrl);
                  message.success('Page URL copied.');
      }
    } catch (error) {
      // Copy URL error
              message.error('An error occurred while copying URL.');
    }
  };

  const footerSelectedItemsForActions = useMemo(() => {
    if (selectedItems.size > 0) return selectedItems;
    return menuDrivenSelection ?? new Set<number>();
  }, [selectedItems, menuDrivenSelection]);

  const footerSelectedItemsDetailsForActions = useMemo(() => {
    if (!entry?.children || !Array.isArray(entry.children)) return [];
    return entry.children
      .filter((item: any) => footerSelectedItemsForActions.has(item.id))
      .map((item: any) => ({
        id: item.id,
        name: item.name,
        entryType: item.entryType,
        status: item.status,
        share: item.share || [],
        path: item.path,
      }));
  }, [entry, footerSelectedItemsForActions]);

  const footerModalsOnly = selectedItems.size === 0 && (menuDrivenSelection?.size ?? 0) > 0;

  const footerCurrentEntryForActions = useMemo(() => {
    if (footerSelectedItemsForActions.size === 0) return entry;
    return entry?.children?.find((item: any) => footerSelectedItemsForActions.has(item.id)) || entry;
  }, [entry, footerSelectedItemsForActions]);

  // Approval modal fonksiyonları
  const handleApprovalConfirm = async (changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      // Modal'ı kapat
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };


  const handleItemRenameSubmit = async (item: any, newName: string) => {
    try {
      if (item.entryType === 'File') {
        await editFile({ id: item.id, name: newName });
      } else if (item.entryType === 'Folder') {
        await editFolder({ id: item.id, name: newName });
      } else {
        message.info('Rename — not supported for this type.');
        setEditingItemId(null);
        return;
      }
      message.success('Renamed.');
      const response = await getEntryByPath(fullPath);
      if (response?.data) setEntry(response.data);
      setEditingItemId(null);
    } catch {
      message.error('An error occurred while renaming.');
      setEditingItemId(null);
    }
  };

  const handleChangeCoverFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !changeCoverTarget) return;
    const shareEmails = (changeCoverTarget.share || []).map((s: any) => typeof s === 'string' ? s : s?.email).filter(Boolean);
    try {
      const payload = { id: changeCoverTarget.id, name: changeCoverTarget.name, thumbnail: file, share: shareEmails };
      let result: any;
      if (changeCoverTarget.entryType === 'File' || changeCoverTarget.entryType === 2) {
        result = await editFile(payload);
      } else if (changeCoverTarget.entryType === 'Folder') {
        result = await editFolder(payload);
      } else if (changeCoverTarget.entryType === 'Project') {
        result = await editProject(payload);
      } else {
        message.error('Unknown entry type.');
        setChangeCoverTarget(null);
        return;
      }
      if (result?.success) {
        message.success('Cover updated.');
        const response = await getEntryByPath(fullPath);
        if (response?.data) setEntry(response.data);
      } else {
        message.error(result?.message || 'Failed to update cover.');
      }
    } catch {
      message.error('An error occurred while updating cover.');
    }
    setChangeCoverTarget(null);
  };

  const handleItemMenuAction = async (action: ProjectItemCardMenuAction, item: any) => {
    if (action === 'delete') {
      setMenuDrivenSelection(new Set([item.id]));
      setOpenDeleteModal(true);
      return;
    }
    if (action === 'rename') {
      setEditingItemId(item.id);
      return;
    }
    if (action === 'changeCover') {
      setChangeCoverTarget(item);
      setTimeout(() => changeCoverInputRef.current?.click(), 0);
      return;
    }
    if (action === 'moveTo') {
      if (!canMoveOrDuplicateEntry(user?.role)) {
        message.warning(t("projects.errors.moveError") ?? "You do not have permission to move.");
        return;
      }
      setMoveToEntry(item);
      setOpenMoveToModal(true);
      return;
    }
    if (action === 'duplicate') {
      if (!canMoveOrDuplicateEntry(user?.role)) {
        message.warning(t("projects.errors.uploadError") ?? "You do not have permission to duplicate.");
        return;
      }
      const sessionId = `duplicate-${Date.now()}-${item.id}`;
      const itemId = `dup-${item.id}-${Date.now()}`;
      const isFolder = item.entryType === 'Folder' || String(item.entryType) === 'Folder';
      setUploadSessions((prev) => [
        ...prev,
        {
          id: sessionId,
          items: [{ id: itemId, name: item.name, relativePath: item.name, status: 'uploading', progress: 0 }],
          isFolderDrop: isFolder,
          isComplete: false,
          contextPath: fullPath,
          sessionType: 'duplicate',
        },
      ]);
      duplicateEntry({ id: item.id, entryType: item.entryType ?? 'File' })
        .then((result) => {
          if (result?.success) {
            setUploadSessions((prev) => prev.filter((s) => s.id !== sessionId));
            handleRefresh(true);
          } else {
            setUploadSessions((prev) =>
              prev.map((s) =>
                s.id !== sessionId
                  ? s
                  : {
                      ...s,
                      items: s.items.map((it) => ({
                        ...it,
                        status: 'error',
                        progress: 100,
                        error: result?.message || t('projects.errors.uploadError'),
                      })),
                      isComplete: true,
                    }
              )
            );
          }
        })
        .catch(() => {
          setUploadSessions((prev) =>
            prev.map((s) =>
              s.id !== sessionId
                ? s
                : {
                    ...s,
                    items: s.items.map((it) => ({ ...it, status: 'error', progress: 100, error: t('projects.errors.uploadError') })),
                    isComplete: true,
                  }
            )
          );
        });
      return;
    }
    if (action === 'share') {
      setMenuDrivenSelection(new Set([item.id]));
      setOpenShareModal(true);
      return;
    }
    if (action === 'download') {
      try {
        const fetchers: DownloadItemsFetchers = { getFileById, getDownloadLinks: getDownloadLinks as DownloadItemsFetchers['getDownloadLinks'] };
        const items = await getDownloadItemsForEntry(
          { id: item.id, path: item.path, entryType: item.entryType },
          fetchers
        );
        if (items.length > 0) await triggerDownloadsFromItems(items);
      } catch (e) {
        console.error('[Download] Card menu error:', e);
      }
      return;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const folderIdForUpload: number | null = entry && (entry.entryType === 'Folder' || String(entry.entryType) === 'Folder') ? entry.id : null;

  const runCreateFileQueue = async (
    sessionId: string,
    filesWithPaths: { file: File; relativePath: string }[],
    pid: number,
    fid: number | null,
    contextPath: string
  ) => {
    const initialItems: UploadProgressItem[] = filesWithPaths.map((f, i) => ({
      id: `upload-${Date.now()}-${i}-${f.relativePath}`,
      name: f.file.name,
      relativePath: f.relativePath,
      status: 'pending' as const,
      progress: 0,
    }));
    uploadCancelRefs.current[sessionId] = false;
    setUploadSessions((prev) => [...prev, { id: sessionId, items: initialItems, isFolderDrop: false, isComplete: false, contextPath }]);

    const updateItem = (id: string, patch: Partial<UploadProgressItem>) => {
      setUploadSessions((prev) =>
        prev.map((s) => (s.id !== sessionId ? s : { ...s, items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) }))
      );
    };

    const yieldToPaint = () => new Promise<void>((r) => setTimeout(r, 0));
    const createdEntries: any[] = [];

    for (let i = 0; i < initialItems.length; i++) {
      if (uploadCancelRefs.current[sessionId]) break;
      const { file, relativePath } = filesWithPaths[i];
      const item = initialItems[i];
      updateItem(item.id, { status: 'uploading', progress: 0 });
      await yieldToPaint();
      let thumbnail: Blob | undefined;
      if (file.type.startsWith('video/')) {
        thumbnail = (await getVideoThumbnailAtSecond(file, 1)) ?? undefined;
      } else if (file.type.startsWith('image/')) {
        thumbnail = file;
      }
      const result = await createFile({
        name: fileNameWithoutExtension(relativePath || file.name),
        file,
        projectId: pid,
        folderId: fid ?? undefined,
        status: ApiFilePostStatusEnum.NeedsReview,
        share: [],
        thumbnail,
      });
      if (uploadCancelRefs.current[sessionId]) break;
      if (result?.success !== true) {
        updateItem(item.id, { status: 'error', progress: 100, error: (result as any)?.message || t('projects.errors.uploadError') });
      } else {
        updateItem(item.id, { status: 'done', progress: 100 });
        const data = (result as any)?.data ?? (result as any)?.Data;
        if (data && (data.id || data.Id)) {
          createdEntries.push({
            ...data,
            id: data.id ?? data.Id,
            name: data.name ?? data.Name ?? file.name,
            entryType: data.entryType ?? data.EntryType ?? 'File',
          });
        }
      }
      await yieldToPaint();
    }

    setUploadSessions((prev) => prev.map((s) => (s.id !== sessionId ? s : { ...s, isComplete: true })));
    if (currentPathRef.current === contextPath) {
      if (createdEntries.length > 0) {
        setEntry((prev: any) => (prev ? { ...prev, children: [...(prev.children || []), ...createdEntries] } : prev));
      }
      handleRefresh(true);
    }
    setUploadSessions((prev) => prev.filter((s) => s.id !== sessionId));
    delete uploadCancelRefs.current[sessionId];
  };

  const runUploadQueue = async (
    sessionId: string,
    filesWithPaths: { file: File; relativePath: string }[],
    hadFolder: boolean,
    pid: number,
    fid: number | null,
    contextPath: string
  ) => {
    const initialItems: UploadProgressItem[] = filesWithPaths.map((f, i) => ({
      id: `upload-${Date.now()}-${i}-${f.relativePath}`,
      name: f.file.name,
      relativePath: f.relativePath,
      status: 'pending' as const,
      progress: 0,
    }));
    uploadCancelRefs.current[sessionId] = false;
    setUploadSessions((prev) => [...prev, { id: sessionId, items: initialItems, isFolderDrop: hadFolder, isComplete: false, contextPath }]);

    const updateItem = (id: string, patch: Partial<UploadProgressItem>) => {
      setUploadSessions((prev) =>
        prev.map((s) => (s.id !== sessionId ? s : { ...s, items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) }))
      );
    };

    const yieldToPaint = () => new Promise<void>((r) => setTimeout(r, 0));
    const createdEntries: any[] = [];

    for (let i = 0; i < initialItems.length; i++) {
      if (uploadCancelRefs.current[sessionId]) break;
      const { file, relativePath } = filesWithPaths[i];
      const item = initialItems[i];
      updateItem(item.id, { status: 'uploading', progress: 0 });
      await yieldToPaint();
      let thumbnail: Blob | undefined;
      if (file.type.startsWith('video/')) {
        thumbnail = (await getVideoThumbnailAtSecond(file, 1)) ?? undefined;
      } else if (file.type.startsWith('image/')) {
        thumbnail = file;
      }
      const result = await uploadWithPath({
        projectId: pid,
        folderId: fid,
        relativePath,
        file,
        ...(thumbnail ? { thumbnail } : {}),
        onProgress: (p) => updateItem(item.id, { progress: p }),
      });
      if (uploadCancelRefs.current[sessionId]) break;
      if (result?.success !== true) {
        updateItem(item.id, { status: 'error', progress: 100, error: (result as any)?.message || t('projects.errors.uploadError') });
      } else {
        updateItem(item.id, { status: 'done', progress: 100 });
        const data = (result as any)?.data ?? (result as any)?.Data;
        if (data && (data.id || data.Id)) {
          createdEntries.push({
            ...data,
            id: data.id ?? data.Id,
            name: data.name ?? data.Name ?? file.name,
            entryType: data.entryType ?? data.EntryType ?? 'File',
          });
        }
      }
      await yieldToPaint();
    }

    setUploadSessions((prev) => prev.map((s) => (s.id !== sessionId ? s : { ...s, isComplete: true })));
    if (currentPathRef.current === contextPath) {
      if (createdEntries.length > 0) {
        setEntry((prev: any) => (prev ? { ...prev, children: [...(prev.children || []), ...createdEntries] } : prev));
      }
      handleRefresh(true);
    }
    setUploadSessions((prev) => prev.filter((s) => s.id !== sessionId));
    delete uploadCancelRefs.current[sessionId];
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (user?.role === Role.Viewer) return;
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const internalMovePayload = readEntryMoveDragData(e.dataTransfer);
    if (internalMovePayload != null && e.dataTransfer.files.length === 0) {
      return;
    }
    if (user?.role === Role.Viewer || !projectId) return;
    const dt = e.dataTransfer;
    if (!dt?.items?.length && !dt?.files?.length) return;
    try {
      const { filesWithPaths } = await getFilesWithRelativePaths(dt);
      const valid: { file: File; relativePath: string }[] = [];
      let skipped = 0;
      for (const { file, relativePath } of filesWithPaths) {
        if (file.size > MAX_FILE_SIZE) skipped++;
        else valid.push({ file, relativePath });
      }
      if (skipped > 0) {
        message.warning(t('projects.fileUpload.fileSizeExceeded'));
      }
      if (valid.length > 0) {
        const allowedSet = new Set(ALLOWED_UPLOAD_EXTENSIONS);
        const allowedOnly: { file: File; relativePath: string }[] = [];
        const disallowedExtensions = new Set<string>();
        for (const item of valid) {
          const fileName = item.relativePath.split('/').pop() || item.file.name || '';
          const ext = getFileExtension(fileName);
          if (ext && allowedSet.has(ext)) {
            allowedOnly.push(item);
          } else if (ext) {
            disallowedExtensions.add(ext);
          }
        }
        if (disallowedExtensions.size > 0 && allowedOnly.length === 0) {
          setUploadFileTypeWarningDisallowed(Array.from(disallowedExtensions).sort());
          setUploadFileTypeWarningVisible(true);
        }
        if (allowedOnly.length === 0) {
          return;
        }
        const usedLowercase = new Set<string>(
          (entry?.children || [])
            .map((c: any) => String(c?.name ?? '').trim().toLowerCase())
            .filter((s: string) => s.length > 0)
        );
        const uniqueTopLevels = Array.from(
          new Set(
            allowedOnly
              .map((i) => i.relativePath.split('/')[0] ?? '')
              .filter((s) => s.length > 0)
          )
        ).sort((a, b) => a.localeCompare(b));
        const topRename = new Map<string, string>();
        for (const top of uniqueTopLevels) {
          const mode = dropTopLevelModeForName(top, allowedOnly);
          topRename.set(top, allocateUniqueDropName(top, usedLowercase, mode));
        }
        const resolvedAllowed = allowedOnly.map((item) => {
          const parts = item.relativePath.split('/');
          const oldTop = parts[0] ?? '';
          const newTop = topRename.get(oldTop) ?? oldTop;
          const newPath = parts.length <= 1 ? newTop : [newTop, ...parts.slice(1)].join('/');
          return { ...item, relativePath: newPath };
        });
        const rootFiles = resolvedAllowed.filter(({ relativePath }) => !relativePath.includes('/'));
        const withFolderPath = resolvedAllowed.filter(({ relativePath }) => relativePath.includes('/'));
        const folderGroups = new Map<string, { file: File; relativePath: string }[]>();
        for (const item of withFolderPath) {
          const topLevel = item.relativePath.split('/')[0] ?? '';
          if (!folderGroups.has(topLevel)) folderGroups.set(topLevel, []);
          folderGroups.get(topLevel)!.push(item);
        }
        const baseId = `upload-${Date.now()}`;
        Array.from(folderGroups.values()).forEach((files, folderIndex) => {
          if (files.length > 0) {
            runUploadQueue(`${baseId}-folder-${folderIndex}`, files, true, projectId, folderIdForUpload, fullPath);
          }
        });
        if (rootFiles.length > 0) {
          runUploadQueue(`${baseId}-file`, rootFiles, false, projectId, folderIdForUpload, fullPath);
        }
      }
    } catch (err) {
      message.error(t('projects.errors.uploadError'));
    }
  };

  const handleUploadCancel = (sessionId: string) => {
    uploadCancelRefs.current[sessionId] = true;
  };

  const handleUploadClose = (sessionId: string) => {
    setUploadSessions((prev) => prev.filter((s) => s.id !== sessionId));
    delete uploadCancelRefs.current[sessionId];
  };

  if (loading) {
    return null;
  }

  if (pathAccessDenied) {
    return (
      <MainLayout>
        <Row className={styles.projectDetailEmptyWrap}>
          <Col span={24}>
            <ProjectFolderEmptyState
              variant={folderCardVariant}
              messageOnly
              title={t('projectDetail.accessDenied')}
              hint={t('projectDetail.accessDeniedHint')}
              actionLabel={t('projectDetail.back')}
              onAction={handleGoBack}
            />
          </Col>
        </Row>
      </MainLayout>
    );
  }

  // Path'te 2 veya daha fazla segment varsa VE entry bir dosya ise FileDetail render et
  const pathSegments = fullPath.split('/').filter(p => p && p.trim() !== '');
  const isFile = entry && (entry.entryType === 'File' || entry.entryType === 2 || String(entry.entryType) === 'File');

  // entry tipi File ise segment sayısından bağımsız olarak FileDetail göster
  if (isFile) {
    return (
      <HeaderlessLayout title={entry?.name || pathSegments[1] || 'File'} icon={<ProjectOutlined />} forceInitialCollapse={true}>
        
        <FileDetail breadcrumbPathParts={pathParts} bclocation={location} fileDetail={entry} onBack={handleGoBack} />
      </HeaderlessLayout>
    );
  }

  if (!entry) {
    return (
      <MainLayout>
        <Row className={styles.projectDetailEmptyWrap}>
          <Col span={24}>
            <ProjectFolderEmptyState
              variant={folderCardVariant}
              messageOnly
              title={t('projectDetail.projectNotFound')}
              hint={t('projectDetail.projectNotFoundHint')}
              actionLabel={t('breadcrumb.projects')}
              onAction={() => navigate(getProjectsBasePath())}
            />
          </Col>
        </Row>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      headerLeft={
        <div className={styles.projectDetailHeaderWrapper}>
          <Breadcrumb />
          <div className={styles.projectDetailHeaderRow}>
            {user?.role !== Role.Viewer && (
              <NewEntrySection
                userRole={user?.role}
                isPopoverOpen={isEntryPopoverOpen}
                onPopoverOpenChange={setIsEntryPopoverOpen}
                onCreateFolder={handleCreateFolder}
                onCreateFile={triggerFileUpload}
              />
            )}
          </div>
        </div>
      }
    >
        {user?.role !== Role.Viewer ? (
          <Dropdown
            trigger={['contextMenu']}
            menu={{ items: contentAreaMenuItems, onClick: handleContentAreaMenuClick }}
            overlayClassName={folderCardVariant === 'external' ? styles.projectDetailContextMenuExternal : styles.projectDetailContextMenu}
            getPopupContainer={() => document.querySelector('[class*="contentBody"]') || document.body}
          >
            <div
              className={styles.projectDetailContentArea}
              onContextMenu={(e) => e.preventDefault()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {Array.isArray(entry.children) && entry.children.length > 0 ? (
                <>
                  <div className={styles.projectDetailToolbar}>
                    <div className={styles.projectDetailToolbarSegment}>
                    <Dropdown
                      trigger={['click']}
                      open={openFilterDropdown}
                      onOpenChange={(open) => { setOpenFilterDropdown(open); if (open) setOpenSortDropdown(false); }}
                      getPopupContainer={() => document.body}
                      align={{ offset: [0, 10] }}
                      overlayClassName={`${styles.projectDetailFilterDropdownOverlay}${folderCardVariant === 'external' ? ` ${styles.projectDetailFilterDropdownOverlayExternal}` : ''}`}
                      dropdownRender={() => (
                        <div
                          className={`${styles.projectDetailFilterDropdown} ${folderCardVariant === 'external' ? styles.projectDetailFilterDropdownExternal : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.projectDetailFilterRow}>
                            <CalendarOutlined className={styles.projectDetailFilterIcon} />
                            <span className={styles.projectDetailFilterLabel}>{t('projectDetail.date')}</span>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Select
                                className={styles.projectDetailFilterSelect}
                                popupClassName={sortSelectPopupClassName}
                                getPopupContainer={() => document.body}
                                placeholder={t('projectDetail.date')}
                                allowClear
                                value={filterDateType || undefined}
                                onChange={(v) => setFilterDateType(v ?? '')}
                                suffixIcon={<DownOutlined />}
                                options={[
                                  { value: 'month', label: t('projectDetail.dateMonth') },
                                  { value: 'quarter', label: t('projectDetail.dateQuarter') },
                                  { value: 'year', label: t('projectDetail.dateYear') },
                                ]}
                              />
                            </div>
                          </div>
                          <div className={styles.projectDetailFilterRow}>
                            <TeamOutlined className={styles.projectDetailFilterIcon} />
                            <span className={styles.projectDetailFilterLabel}>{t('projectDetail.organization')}</span>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Input
                                className={styles.projectDetailFilterInput}
                                placeholder={t('projectDetail.organizationName')}
                                value={filterOrganization}
                                onChange={(e) => setFilterOrganization(e.target.value)}
                                allowClear
                              />
                            </div>
                          </div>
                          <div className={styles.projectDetailFilterRow}>
                            <UserOutlined className={styles.projectDetailFilterIcon} />
                            <span className={styles.projectDetailFilterLabel}>{t('projectDetail.name')}</span>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Input
                                className={styles.projectDetailFilterInput}
                                placeholder={t('projectDetail.userName')}
                                value={filterName}
                                onChange={(e) => setFilterName(e.target.value)}
                                allowClear
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    >
                      <Button type="default" className={`${styles.projectDetailToolbarButton} ${openFilterDropdown ? styles.projectDetailToolbarButtonActive : ''}`}>
                        <span className={styles.projectDetailToolbarButtonContent}>
                          <FilterOutlined />
                          <span>{t('common.filter')}</span>
                        </span>
                      </Button>
                    </Dropdown>
                    <Dropdown
                      trigger={['click']}
                      open={openSortDropdown}
                      onOpenChange={(open) => { setOpenSortDropdown(open); if (open) setOpenFilterDropdown(false); }}
                      getPopupContainer={() => document.body}
                      align={{ offset: [0, 10] }}
                      overlayClassName={`${styles.projectDetailSortDropdownOverlay}${folderCardVariant === 'external' ? ` ${styles.projectDetailSortDropdownOverlayExternal}` : ''}`}
                      dropdownRender={() => {
                        const directionOptions = !sortField ? [] : sortField === 'creationDate'
                          ? [{ value: 'desc', label: t('projectDetail.sortDirNewToOld') }, { value: 'asc', label: t('projectDetail.sortDirOldToNew') }]
                          : sortField === 'organization' || sortField === 'name' || sortField === 'status'
                            ? [{ value: 'asc', label: t('projectDetail.sortDirAToZ') }, { value: 'desc', label: t('projectDetail.sortDirZToA') }]
                            : [{ value: 'asc', label: t('projectDetail.sortDirAsc') }, { value: 'desc', label: t('projectDetail.sortDirDesc') }];
                        return (
                        <div
                          className={`${styles.projectDetailSortDropdown} ${folderCardVariant === 'external' ? styles.projectDetailSortDropdownExternal : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.projectDetailSortDropdownRow}>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Select
                                className={styles.projectDetailSortSelect}
                                popupClassName={sortSelectPopupClassName}
                                getPopupContainer={() => document.body}
                                value={sortField || undefined}
                                onChange={(v) => {
                                  const next = (v ?? '') as typeof sortField;
                                  setSortField(next);
                                  setSortDirection(defaultSortDirectionForField(next));
                                }}
                                placeholder={t('projectDetail.sortBy')}
                                allowClear
                                suffixIcon={<DownOutlined />}
                                options={[
                                  { value: 'creationDate', label: t('projectDetail.sortFieldCreationDate') },
                                  { value: 'organization', label: t('projectDetail.sortFieldOrganization') },
                                  { value: 'name', label: t('projectDetail.sortFieldName') },
                                  { value: 'numberOfFiles', label: t('projectDetail.sortFieldNumberOfFiles') },
                                  { value: 'status', label: t('projectDetail.sortFieldStatus') },
                                ]}
                              />
                            </div>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Select
                                className={styles.projectDetailSortSelect}
                                popupClassName={sortSelectPopupClassName}
                                getPopupContainer={() => document.body}
                                value={sortDirection || undefined}
                                onChange={(v) => setSortDirection((v ?? '') as 'asc' | 'desc')}
                                placeholder={t('projectDetail.sortDirNewToOld')}
                                allowClear
                                disabled={!sortField}
                                suffixIcon={<DownOutlined />}
                                options={directionOptions}
                              />
                            </div>
                          </div>
                        </div>
                        );
                      }}
                    >
                      <Button type="default" className={`${styles.projectDetailToolbarButton} ${openSortDropdown ? styles.projectDetailToolbarButtonActive : ''}`}>
                        <span className={styles.projectDetailToolbarButtonContent}>
                          <MenuOutlined />
                          <span>{t('projectDetail.sortBy')}</span>
                        </span>
                      </Button>
                    </Dropdown>
                    </div>
                    <span className={styles.projectDetailToolbarCount}>
                      {(filteredAssets.length + filteredFolders.length)} {t('projectDetail.totalEntryCount')} <span className={styles.projectDetailToolbarSize}>{formatBytes(totalSizeBytes)}</span>
                    </span>
                  </div>
                  {(filteredAssets.length > 0 || currentPathSessions.some((s) => !s.isFolderDrop)) && (
                          <div className={styles.projectDetailSection}>
                            <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Assets</h3>
                            <div className={styles.projectDetailGrid}>
                              {filteredAssets.map((item: any) => (
                                <div key={item.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                                <ProjectItemCard
                                  item={item}
                                  isSelected={selectedItems.has(item.id)}
                                  onSelectChange={(checked: boolean) => handleItemSelect(item.id, checked)}
                                  onNavigate={handleNavigation}
                                  formatDate={formatDate}
                                  selectable={user?.role !== Role.Viewer}
                                  variant={folderCardVariant}
                                  onMenuAction={handleItemMenuAction}
                                  editingItemId={editingItemId}
                                  onRenameSubmit={handleItemRenameSubmit}
                                  onRenameCancel={() => setEditingItemId(null)}
                                  entryMoveDnd={
                                    !canMoveOrDuplicate
                                      ? null
                                      : { ...entryMoveDndBase, isActiveMoveDropTarget: dragOverFolderId === item.id }
                                  }
                                  allowMoveToAndDuplicate={canMoveOrDuplicate}
                                />
                                </div>
                              ))}
                              {currentPathSessions.filter((s) => !s.isFolderDrop).map((session) => (
                                <div key={session.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                                  <UploadProgressCard
                                    items={session.items}
                                    onCancel={session.sessionType === 'duplicate' ? undefined : () => handleUploadCancel(session.id)}
                                    isActive={session.items.some((i) => i.status === 'uploading' || i.status === 'pending') || (session.sessionType === 'duplicate' && !session.isComplete)}
                                    onClose={() => handleUploadClose(session.id)}
                                    isComplete={session.isComplete}
                                    inline
                                    variant="entry"
                                    cardVariant={folderCardVariant}
                                    title={session.sessionType === 'duplicate' ? t('uploadProgress.duplicating') : undefined}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                  )}
                  {(filteredFolders.length > 0 || currentPathSessions.some((s) => s.isFolderDrop)) && (
                          <div className={styles.projectDetailSection}>
                            <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Folders</h3>
                            <div className={styles.projectDetailGrid}>
                              {filteredFolders.map((item: any) => (
                                <div key={item.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                                <ProjectItemCard
                                  item={item}
                                  isSelected={selectedItems.has(item.id)}
                                  onSelectChange={(checked: boolean) => handleItemSelect(item.id, checked)}
                                  onNavigate={handleNavigation}
                                  formatDate={formatDate}
                                  selectable={user?.role !== Role.Viewer}
                                  variant={folderCardVariant}
                                  onMenuAction={handleItemMenuAction}
                                  editingItemId={editingItemId}
                                  onRenameSubmit={handleItemRenameSubmit}
                                  onRenameCancel={() => setEditingItemId(null)}
                                  entryMoveDnd={
                                    !canMoveOrDuplicate
                                      ? null
                                      : { ...entryMoveDndBase, isActiveMoveDropTarget: dragOverFolderId === item.id }
                                  }
                                  allowMoveToAndDuplicate={canMoveOrDuplicate}
                                />
                                </div>
                              ))}
                              {currentPathSessions.filter((s) => s.isFolderDrop).map((session) => (
                                <div key={session.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                                  <UploadProgressCard
                                    items={session.items}
                                    onCancel={session.sessionType === 'duplicate' ? undefined : () => handleUploadCancel(session.id)}
                                    isActive={session.items.some((i) => i.status === 'uploading' || i.status === 'pending') || (session.sessionType === 'duplicate' && !session.isComplete)}
                                    onClose={() => handleUploadClose(session.id)}
                                    isComplete={session.isComplete}
                                    inline
                                    variant="folder"
                                    cardVariant={folderCardVariant}
                                    title={session.sessionType === 'duplicate' ? t('uploadProgress.duplicating') : undefined}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                  )}
                  {filteredFolders.length === 0 && filteredAssets.length === 0 && currentPathSessions.length === 0 &&
                    renderEmptyFolderState()}
                </>
              ) : currentPathSessions.length > 0 ? (
                <>
                  <div className={styles.projectDetailToolbar}>
                    <div className={styles.projectDetailToolbarSegment} />
                    <span className={styles.projectDetailToolbarCount}>0 {t('projectDetail.totalEntryCount')} <span className={styles.projectDetailToolbarSize}>{formatBytes(totalSizeBytes)}</span></span>
                  </div>
                  {currentPathSessions.some((s) => !s.isFolderDrop) && (
                    <div className={styles.projectDetailSection}>
                      <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Assets</h3>
                      <div className={styles.projectDetailGrid}>
                        {currentPathSessions.filter((s) => !s.isFolderDrop).map((session) => (
                          <div key={session.id} className={styles.projectDetailGridCell}>
                            <UploadProgressCard
                              items={session.items}
                              onCancel={session.sessionType === 'duplicate' ? undefined : () => handleUploadCancel(session.id)}
                              isActive={session.items.some((i) => i.status === 'uploading' || i.status === 'pending') || (session.sessionType === 'duplicate' && !session.isComplete)}
                              onClose={() => handleUploadClose(session.id)}
                              isComplete={session.isComplete}
                              inline
                              variant="entry"
                              cardVariant={folderCardVariant}
                              title={session.sessionType === 'duplicate' ? t('uploadProgress.duplicating') : undefined}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {currentPathSessions.some((s) => s.isFolderDrop) && (
                    <div className={styles.projectDetailSection}>
                      <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Folders</h3>
                      <div className={styles.projectDetailGrid}>
                        {currentPathSessions.filter((s) => s.isFolderDrop).map((session) => (
                          <div key={session.id} className={styles.projectDetailGridCell}>
                            <UploadProgressCard
                              items={session.items}
                              onCancel={() => handleUploadCancel(session.id)}
                              isActive={session.items.some((i) => i.status === 'uploading' || i.status === 'pending')}
                              onClose={() => handleUploadClose(session.id)}
                              isComplete={session.isComplete}
                              inline
                              variant="folder"
                              cardVariant={folderCardVariant}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                renderEmptyFolderState()
              )}
            </div>
          </Dropdown>
        ) : (
          <>
            {Array.isArray(entry.children) && entry.children.length > 0 ? (
              <>
                <div className={styles.projectDetailToolbar}>
                  <div className={styles.projectDetailToolbarSegment}>
                  <Dropdown
                    trigger={['click']}
                    open={openFilterDropdown}
                    onOpenChange={(open) => { setOpenFilterDropdown(open); if (open) setOpenSortDropdown(false); }}
                    getPopupContainer={() => document.body}
                    align={{ offset: [0, 10] }}
                    overlayClassName={`${styles.projectDetailFilterDropdownOverlay}${folderCardVariant === 'external' ? ` ${styles.projectDetailFilterDropdownOverlayExternal}` : ''}`}
                    dropdownRender={() => (
                      <div
                        className={`${styles.projectDetailFilterDropdown} ${folderCardVariant === 'external' ? styles.projectDetailFilterDropdownExternal : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className={styles.projectDetailFilterRow}>
                          <CalendarOutlined className={styles.projectDetailFilterIcon} />
                          <span className={styles.projectDetailFilterLabel}>{t('projectDetail.date')}</span>
                          <div className={styles.projectDetailFilterControlShell}>
                            <Select
                              className={styles.projectDetailFilterSelect}
                              popupClassName={sortSelectPopupClassName}
                              getPopupContainer={() => document.body}
                              placeholder={t('projectDetail.date')}
                              allowClear
                              value={filterDateType || undefined}
                              onChange={(v) => setFilterDateType(v ?? '')}
                              suffixIcon={<DownOutlined />}
                              options={[
                                { value: 'month', label: t('projectDetail.dateMonth') },
                                { value: 'quarter', label: t('projectDetail.dateQuarter') },
                                { value: 'year', label: t('projectDetail.dateYear') },
                              ]}
                            />
                          </div>
                        </div>
                        <div className={styles.projectDetailFilterRow}>
                          <TeamOutlined className={styles.projectDetailFilterIcon} />
                          <span className={styles.projectDetailFilterLabel}>{t('projectDetail.organization')}</span>
                          <div className={styles.projectDetailFilterControlShell}>
                            <Input
                              className={styles.projectDetailFilterInput}
                              placeholder={t('projectDetail.organizationName')}
                              value={filterOrganization}
                              onChange={(e) => setFilterOrganization(e.target.value)}
                              allowClear
                            />
                          </div>
                        </div>
                        <div className={styles.projectDetailFilterRow}>
                          <UserOutlined className={styles.projectDetailFilterIcon} />
                          <span className={styles.projectDetailFilterLabel}>{t('projectDetail.name')}</span>
                          <div className={styles.projectDetailFilterControlShell}>
                            <Input
                              className={styles.projectDetailFilterInput}
                              placeholder={t('projectDetail.userName')}
                              value={filterName}
                              onChange={(e) => setFilterName(e.target.value)}
                              allowClear
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  >
                    <Button type="default" className={`${styles.projectDetailToolbarButton} ${openFilterDropdown ? styles.projectDetailToolbarButtonActive : ''}`}>
                      <span className={styles.projectDetailToolbarButtonContent}>
                        <FilterOutlined />
                        <span>{t('common.filter')}</span>
                      </span>
                    </Button>
                  </Dropdown>
                  <Dropdown
                    trigger={['click']}
                    open={openSortDropdown}
                    onOpenChange={(open) => { setOpenSortDropdown(open); if (open) setOpenFilterDropdown(false); }}
                    getPopupContainer={() => document.body}
                    align={{ offset: [0, 10] }}
                    overlayClassName={`${styles.projectDetailSortDropdownOverlay}${folderCardVariant === 'external' ? ` ${styles.projectDetailSortDropdownOverlayExternal}` : ''}`}
                    dropdownRender={() => {
                        const directionOptions = !sortField ? [] : sortField === 'creationDate'
                          ? [{ value: 'desc', label: t('projectDetail.sortDirNewToOld') }, { value: 'asc', label: t('projectDetail.sortDirOldToNew') }]
                          : sortField === 'organization' || sortField === 'name' || sortField === 'status'
                            ? [{ value: 'asc', label: t('projectDetail.sortDirAToZ') }, { value: 'desc', label: t('projectDetail.sortDirZToA') }]
                            : [{ value: 'asc', label: t('projectDetail.sortDirAsc') }, { value: 'desc', label: t('projectDetail.sortDirDesc') }];
                        return (
                        <div
                          className={`${styles.projectDetailSortDropdown} ${folderCardVariant === 'external' ? styles.projectDetailSortDropdownExternal : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.projectDetailSortDropdownRow}>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Select
                                className={styles.projectDetailSortSelect}
                                popupClassName={sortSelectPopupClassName}
                                getPopupContainer={() => document.body}
                                value={sortField || undefined}
                                onChange={(v) => {
                                  const next = (v ?? '') as typeof sortField;
                                  setSortField(next);
                                  setSortDirection(defaultSortDirectionForField(next));
                                }}
                                placeholder={t('projectDetail.sortBy')}
                                allowClear
                                suffixIcon={<DownOutlined />}
                                options={[
                                  { value: 'creationDate', label: t('projectDetail.sortFieldCreationDate') },
                                  { value: 'organization', label: t('projectDetail.sortFieldOrganization') },
                                  { value: 'name', label: t('projectDetail.sortFieldName') },
                                  { value: 'numberOfFiles', label: t('projectDetail.sortFieldNumberOfFiles') },
                                  { value: 'status', label: t('projectDetail.sortFieldStatus') },
                                ]}
                              />
                            </div>
                            <div className={styles.projectDetailFilterControlShell}>
                              <Select
                                className={styles.projectDetailSortSelect}
                                popupClassName={sortSelectPopupClassName}
                                getPopupContainer={() => document.body}
                                value={sortDirection || undefined}
                                onChange={(v) => setSortDirection((v ?? '') as 'asc' | 'desc')}
                                placeholder={t('projectDetail.sortDirNewToOld')}
                                allowClear
                                disabled={!sortField}
                                suffixIcon={<DownOutlined />}
                                options={directionOptions}
                              />
                            </div>
                          </div>
                        </div>
                        );
                      }}
                  >
                    <Button type="default" className={`${styles.projectDetailToolbarButton} ${openSortDropdown ? styles.projectDetailToolbarButtonActive : ''}`}>
                      <span className={styles.projectDetailToolbarButtonContent}>
                        <MenuOutlined />
                        <span>{t('projectDetail.sortBy')}</span>
                      </span>
                    </Button>
                  </Dropdown>
                  </div>
                  <span className={styles.projectDetailToolbarCount}>
                    {(filteredAssets.length + filteredFolders.length)} {t('projectDetail.totalEntryCount')} <span className={styles.projectDetailToolbarSize}>{formatBytes(totalSizeBytes)}</span>
                  </span>
                </div>
                {filteredAssets.length > 0 && (
                        <div className={styles.projectDetailSection}>
                          <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Assets</h3>
                          <div className={styles.projectDetailGrid}>
                            {filteredAssets.map((item: any) => (
                              <div key={item.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                              <ProjectItemCard
                                item={item}
                                isSelected={selectedItems.has(item.id)}
                                onSelectChange={(checked: boolean) => handleItemSelect(item.id, checked)}
                                onNavigate={handleNavigation}
                                formatDate={formatDate}
                                selectable={false}
                                variant={folderCardVariant}
                                onMenuAction={handleItemMenuAction}
                                editingItemId={editingItemId}
                                onRenameSubmit={handleItemRenameSubmit}
                                onRenameCancel={() => setEditingItemId(null)}
                                allowMoveToAndDuplicate={canMoveOrDuplicate}
                              />
                              </div>
                            ))}
                          </div>
                        </div>
                )}
                {filteredFolders.length > 0 && (
                        <div className={styles.projectDetailSection}>
                          <h3 className={`${styles.projectDetailSectionTitle} ${folderCardVariant === 'external' ? styles.projectDetailSectionTitleExternal : ''}`}>Folders</h3>
                          <div className={styles.projectDetailGrid}>
                            {filteredFolders.map((item: any) => (
                              <div key={item.id} className={styles.projectDetailGridCell} onContextMenu={(e) => e.stopPropagation()}>
                              <ProjectItemCard
                                item={item}
                                isSelected={selectedItems.has(item.id)}
                                onSelectChange={(checked: boolean) => handleItemSelect(item.id, checked)}
                                onNavigate={handleNavigation}
                                formatDate={formatDate}
                                selectable={false}
                                variant={folderCardVariant}
                                onMenuAction={handleItemMenuAction}
                                editingItemId={editingItemId}
                                onRenameSubmit={handleItemRenameSubmit}
                                onRenameCancel={() => setEditingItemId(null)}
                                allowMoveToAndDuplicate={canMoveOrDuplicate}
                              />
                              </div>
                            ))}
                          </div>
                        </div>
                )}
                {filteredFolders.length === 0 && filteredAssets.length === 0 &&
                  renderEmptyFolderState()}
              </>
            ) : (
              renderEmptyFolderState()
            )}
          </>
        )}

      {/* FooterActions — toplu seçim veya kart menüsünden Assign/Delete */}
      {footerSelectedItemsForActions.size > 0 && (
        <FooterActions
          onDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onCopyUrl={handleBulkCopyUrl}
          onApprovalRequest={() => setIsApprovalModalVisible(true)} // Approval modal'ı aç
          fileDetail={null} // ProjectDetail'da fileDetail yok
          modalsOnly={footerModalsOnly}
          onShareOrDeleteModalClosed={() => setMenuDrivenSelection(null)}
          selectedItems={footerSelectedItemsForActions}
          selectedItemsDetails={footerSelectedItemsDetailsForActions}
          currentEntry={footerCurrentEntryForActions}
          hideDownload={false}
          openShareModal={openShareModal}
          onShareModalOpenChange={setOpenShareModal}
          openDeleteModal={openDeleteModal}
          onDeleteModalOpenChange={setOpenDeleteModal}
          variant={folderCardVariant}
          style={{
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
            opacity: 1
          }}
        />
      )}

      {/* Footer Actions için safe alan */}
      <div className={styles.footerSafeArea} />

      <input
        ref={changeCoverInputRef}
        id="project-detail-change-cover-input"
        name="changeCover"
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,image/bmp"
        style={{ display: 'none' }}
        onChange={handleChangeCoverFile}
      />
      <input
        ref={fileInputRef}
        id="project-detail-file-upload-input"
        name="fileUpload"
        type="file"
        multiple
        accept={ALLOWED_UPLOAD_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Edit Modals */}
      <AddNewFileModal
        isOpen={isEditFileModalVisible}
        onClose={() => setIsEditFileModalVisible(false)}
        onSuccess={(newVersionData) => {
          setIsEditFileModalVisible(false);
          handleRefresh();
        }}
        isFromFileDetail={false}
        editMode={true}
        editData={(() => {
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />

      <AddNewFolderModal
        isOpen={isEditFolderModalVisible}
        onClose={() => { setIsEditFolderModalVisible(false); setEditingFolderEntry(null); }}
        onSuccess={() => {
          setIsEditFolderModalVisible(false);
          setEditingFolderEntry(null);
          handleRefresh();
        }}
        editMode={true}
        editData={(() => {
          if (editingFolderEntry) {
            return { ...editingFolderEntry, share: editingFolderEntry.share || [] };
          }
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />

      <AddNewProjectModal
        isOpen={isEditProjectModalVisible}
        onClose={() => setIsEditProjectModalVisible(false)}
        onSuccess={() => {
          setIsEditProjectModalVisible(false);
          handleRefresh();
        }}
        editMode={true}
        variant={folderCardVariant}
        editData={(() => {
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />

      <MoveToModal
        visible={openMoveToModal}
        onCancel={() => { setOpenMoveToModal(false); setMoveToEntry(null); }}
        entryToMove={moveToEntry ? { id: moveToEntry.id, name: moveToEntry.name, path: moveToEntry.path, entryType: moveToEntry.entryType } : undefined}
        initialPath={fullPath}
        onMove={async (targetPath, targetEntry) => {
          if (!moveToEntry?.id) return;
          const sourceEntryType = moveToEntry.entryType === 'Folder' || String(moveToEntry.entryType) === 'Folder' ? 'Folder' : 'File';
          const targetProjectId = targetEntry?.projectId ?? targetEntry?.id;
          const targetParentId =
            targetEntry && (targetEntry.entryType === 'Folder' || String(targetEntry.entryType) === 'Folder')
              ? targetEntry.id
              : null;
          if (targetProjectId == null) {
            message.error(t?.('projects.errors.invalidTarget') ?? 'Invalid destination.');
            return;
          }
          setMoveToLoading(true);
          try {
            const result = await moveEntry({
              sourceId: moveToEntry.id,
              sourceEntryType,
              targetProjectId,
              targetParentId,
            });
            if (result?.success) {
              message.success(t?.('projects.moveSuccess') ?? 'Moved successfully.');
              setOpenMoveToModal(false);
              setMoveToEntry(null);
              handleRefresh(true);
            } else {
              message.error(result?.message ?? (t?.('projects.errors.moveError') ?? 'Failed to move.'));
            }
          } finally {
            setMoveToLoading(false);
          }
        }}
        loading={moveToLoading}
        variant={folderCardVariant}
      />
      
      <UploadLeaveWarningModal
        visible={showLeaveWarningModal}
        onConfirm={() => {
          leaveWarningProceedRef.current?.();
        }}
        onCancel={() => setShowLeaveWarningModal(false)}
        variant={folderCardVariant}
      />

      <UploadFileTypeWarningModal
        visible={uploadFileTypeWarningVisible}
        onClose={() => setUploadFileTypeWarningVisible(false)}
        disallowedTypes={uploadFileTypeWarningDisallowed}
        variant={folderCardVariant}
      />

      {/* Approval Modal */}
      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
        variant={folderCardVariant}
        hasSubentries={
          selectedItems.size > 0 
            ? // Seçili itemlar varsa onların alt öğelerini kontrol et
              entry?.children?.some((child: any) => 
                selectedItems.has(child.id) && (
                  child.entryType === 'Project' || 
                  child.entryType === 'Folder' || 
                  child.entryType === 'File'
                )
              ) || false
            : // Seçili item yoksa mevcut entry'nin children'ını kontrol et
              (entry?.children && entry.children.length > 0) || false
        }
      />

    </MainLayout>
  );
};

export default ProjectDetail; 